import { useMutation, useQuery } from '@apollo/client/react'
import { SEPATransactionMetadataSchema } from '@getcaya/schemas'
import NutrientViewer, {
  type Instance as NutrientInstance,
} from '@nutrient-sdk/viewer'
import { Col, Form, Modal, notification, Row } from 'antd'
import { useEffect, useState } from 'react'
import { ZodError } from 'zod'
import { useLabelizer } from '~app/components/Labelizer/useLabelizer'
import { DocumentLabelType } from '~app/configuration/labels'
import {
  type DocumentFragment,
  FetchDocumentFileTokenDocument,
  type PaymentMetadata,
  UpdateDocumentNatifMetadataDocument,
  UpdateDocumentPaymentMetadataDocument,
  UpdateDocumentRecipientMetadataDocument,
  UpdateDocumentSenderDocument,
  UpdateDocumentSubjectDocument,
} from '~app/graph/generated/graphql'
import { formatLabelText } from '~app/helper/nutrient-label'
import {
  useAutosaveField,
  useAutosaveFields,
  useField,
} from '~app/hooks/useField'
import i18n from '~app/i18n/i18n'
import PDFLoader from '../../components/Labelizer/PDFLoader'
import NatifMetadata from './NatifMetadata'
import DocumentPaymentDetails from './PaymentDetails'
import RecipientMetadata from './RecipientMetadata'
import ScanMetadata from './ScanMetadata'
import DocumentSender, { type Sender } from './Sender'
import TagSelector from './TagSelector'
import DocumentTags from './Tags'
import Title from './Title'

/**
 * Utilities
 */

const paymentSchema = SEPATransactionMetadataSchema({
  fieldRequired: () =>
    i18n.t('documentView.paymentForm.validation.fieldRequired'),
  invalid: field =>
    i18n.t(`documentView.paymentForm.validation.invalid.${field}`),
  invalidCharsForField: () =>
    i18n.t('documentView.paymentForm.validation.invalidCharsForField'),
  maxCharsN: limit =>
    i18n.t('documentView.paymentForm.validation.maxCharsN', { limit }),
  maxDecimalsN: precision =>
    i18n.t('documentView.paymentForm.validation.maxDecimalsN', { precision }),
  minCharsN: limit =>
    i18n.t('documentView.paymentForm.validation.minCharsN', { limit }),
  minNumN: limit =>
    i18n.t('documentView.paymentForm.validation.minNumN', { limit }),
  reservedWord: () =>
    i18n.t('documentView.paymentForm.validation.reservedWord'),
  typeNaN: () => i18n.t('documentView.paymentForm.validation.typeNaN'),
})

export const emptyPayment = {
  iban: null,
  reference: null,
  recipient: null,
  amount: null,
}
/**
 * Styles
 */

const styles: InlineStyles = {
  dialogStyle: {
    top: 0,
    margin: 0,
    maxWidth: '100vw',
    paddingBottom: 0,
  },
}

/**
 * Component
 */

interface Props {
  document: DocumentFragment
  onClose: () => void
  onRemoveDocumentDone?: () => void
}

const DocumentDialog = ({ document, onClose, onRemoveDocumentDone }: Props) => {
  const [instance, setInstance] = useState<NutrientInstance>()
  const [nextLabelType, setNextLabelType] = useState<
    DocumentLabelType | undefined
  >(undefined)
  const [updateSubject] = useMutation(UpdateDocumentSubjectDocument)
  const [updateSender] = useMutation(UpdateDocumentSenderDocument)
  const [updatePaymentMetadata] = useMutation(
    UpdateDocumentPaymentMetadataDocument
  )
  const [updateRecipientMetadata] = useMutation(
    UpdateDocumentRecipientMetadataDocument
  )
  const [updateNatifMetadata] = useMutation(UpdateDocumentNatifMetadataDocument)
  const { data: fileTokenData, loading: loadingFileToken } = useQuery(
    FetchDocumentFileTokenDocument,
    {
      variables: { id: document.id },
    }
  )

  const subject = useField(document.subject)
  const autosaveSubjectField = useAutosaveField(subject, unsavedSubject =>
    updateSubject({
      variables: {
        id: document.id,
        subject: unsavedSubject,
      },
    })
  )
  const sender = document.senderMetadata || {
    name: undefined,
    email: undefined,
    phone: undefined,
  }
  const { fields: senderFields } = useAutosaveFields<Sender>(
    {
      name: useField(sender.name),
      email: useField(sender.email),
      phone: useField(sender.phone),
    },
    unsavedSender =>
      updateSender({
        variables: {
          id: document.id,
          sender: {
            name: unsavedSender.name,
            email: unsavedSender.email,
            phone: unsavedSender.phone,
          },
        },
      })
  )
  const payment = document.paymentMetadata || emptyPayment
  const { fields: paymentFields } = useAutosaveFields<
    Pick<PaymentMetadata, 'iban' | 'reference' | 'recipient' | 'amount'>
  >(
    {
      iban: useField(payment.iban),
      reference: useField(payment.reference),
      recipient: useField(payment.recipient),
      amount: useField(payment.amount),
    },
    unsavedPayment => {
      let validatedPayment: ReturnType<typeof paymentSchema.parse>
      try {
        validatedPayment = paymentSchema.parse(unsavedPayment)
      } catch (error: unknown) {
        if (error instanceof ZodError) {
          for (const issue of error.issues) {
            const key = issue.path[0] as keyof PaymentMetadata
            const field = paymentFields[key]
            if (field) {
              field.setError(issue.message)
            }
          }
        } else {
          notification.error({
            description: 'Error',
            message: error instanceof Error ? error.message : String(error),
            placement: 'bottomRight',
          })
        }
        return Promise.resolve()
      }
      return updatePaymentMetadata({
        variables: {
          documentId: document.id,
          // biome-ignore lint/suspicious/noExplicitAny: ok in mailroom
          data: validatedPayment as any,
        },
      })
    }
  )

  // make recipientMetadata labelizable
  const recipientMetadata = document.recipientMetadata
  const { fields: recipientMetadataFields } = useAutosaveFields<{
    name: string
  }>(
    {
      name: useField(recipientMetadata?.name),
    },
    recipientMetadataValue =>
      updateRecipientMetadata({
        variables: {
          id: document.id,
          recipientMetadata: {
            name: recipientMetadataValue.name,
          },
        },
      })
  )

  // make natifMetadata labelizable
  const natifMetadata = document.natifMetadata
  const { fields: natifMetadataFields } = useAutosaveFields<{
    date: string
    scanId: string
  }>(
    {
      date: useField(natifMetadata?.date),
      scanId: useField(natifMetadata?.scanId),
    },
    natifMetadata =>
      updateNatifMetadata({
        variables: {
          id: document.id,
          natifMetadata,
        },
      })
  )

  const labelableFields = [
    {
      ...paymentFields.iban,
      labelType: DocumentLabelType.Iban,
      saveLabel(labelText: string): Promise<void> {
        const formattedValue = formatLabelText(
          DocumentLabelType.Iban,
          labelText
        )

        try {
          paymentSchema.shape.iban.parse(formattedValue)
          paymentFields.iban.setValue(formattedValue)
        } catch (error: unknown) {
          paymentFields.iban.setValue(formattedValue)
          paymentFields.iban.setError(
            error instanceof ZodError
              ? (error.issues[0]?.message ?? error.message)
              : String(error)
          )
        }
        return Promise.resolve()
      },
    },
    {
      ...paymentFields.amount,
      labelType: DocumentLabelType.Amount,
      saveLabel(labelText: string): Promise<void> {
        const formattedValue = formatLabelText(
          DocumentLabelType.Amount,
          labelText
        )

        try {
          paymentSchema.shape.amount.parse(formattedValue)
          paymentFields.amount.setValue(formattedValue)
        } catch (error: unknown) {
          paymentFields.amount.setValue(formattedValue)
          paymentFields.amount.setError(
            error instanceof ZodError
              ? (error.issues[0]?.message ?? error.message)
              : String(error)
          )
        }
        return Promise.resolve()
      },
    },
    {
      ...paymentFields.reference,
      labelType: DocumentLabelType.Reference,
      saveLabel(labelText: string): Promise<void> {
        try {
          paymentSchema.shape.reference.parse(labelText)
          paymentFields.reference.setValue(labelText)
        } catch (error: unknown) {
          paymentFields.reference.setValue(labelText)
          paymentFields.reference.setError(
            error instanceof ZodError
              ? (error.issues[0]?.message ?? error.message)
              : String(error)
          )
        }
        return Promise.resolve()
      },
    },
    {
      ...paymentFields.recipient,
      labelType: DocumentLabelType.PaymentReceiver,
      saveLabel(labelText: string): Promise<void> {
        try {
          paymentSchema.shape.recipient.parse(labelText)
          paymentFields.recipient.setValue(labelText)
        } catch (error: unknown) {
          paymentFields.recipient.setValue(labelText)
          paymentFields.recipient.setError(
            error instanceof ZodError
              ? (error.issues[0]?.message ?? error.message)
              : String(error)
          )
        }
        return Promise.resolve()
      },
    },
    {
      ...subject,
      labelType: DocumentLabelType.Subject,
      saveLabel: (labelText: string) => {
        const formattedValue = formatLabelText(
          DocumentLabelType.Subject,
          labelText
        )
        return updateSubject({
          variables: {
            id: document.id,
            subject: formattedValue,
          },
        }).then(() => {
          subject.setValue(formattedValue)
        })
      },
    },
    {
      ...senderFields.name,
      labelType: DocumentLabelType.Sender,
      saveLabel: (labelText: string) =>
        updateSender({
          variables: {
            id: document.id,
            sender: {
              name: labelText,
              email: sender.email,
              phone: sender.phone,
            },
          },
        })
          .then(() => {
            senderFields.name.setValue(labelText)
          })
          .catch(() => {
            senderFields.name.setValue(labelText)
          }),
    },
    {
      ...senderFields.email,
      labelType: DocumentLabelType.Email,
      saveLabel: (labelText: string) =>
        updateSender({
          variables: {
            id: document.id,
            sender: {
              name: sender.name,
              email: labelText,
              phone: sender.phone,
            },
          },
        })
          .then(() => {
            senderFields.email.setValue(labelText)
          })
          .catch(() => {
            senderFields.email.setValue(labelText)
          }),
    },
    {
      ...senderFields.phone,
      labelType: DocumentLabelType.Phone,
      saveLabel: (labelText: string) =>
        updateSender({
          variables: {
            id: document.id,
            sender: {
              name: sender.name,
              email: sender.email,
              phone: labelText,
            },
          },
        })
          .then(() => {
            senderFields.phone.setValue(labelText)
          })
          .catch(() => {
            senderFields.phone.setValue(labelText)
          }),
    },
    {
      ...recipientMetadataFields.name,
      labelType: DocumentLabelType.Recipient,
      saveLabel: (labelText: string) =>
        updateRecipientMetadata({
          variables: {
            id: document.id,
            recipientMetadata: { ...recipientMetadata, name: labelText },
          },
        })
          .then(() => {
            recipientMetadataFields.name.setValue(labelText)
          })
          .catch(() => {
            recipientMetadataFields.name.setValue(labelText)
          }),
    },
    {
      ...natifMetadataFields.scanId,
      labelType: DocumentLabelType.TrackingId,
      saveLabel: (labelText: string) =>
        updateNatifMetadata({
          variables: {
            id: document.id,
            natifMetadata: { ...natifMetadata, scanId: labelText },
          },
        })
          .then(() => {
            natifMetadataFields.scanId.setValue(labelText)
          })
          .catch(() => {
            natifMetadataFields.scanId.setValue(labelText)
          }),
    },
    {
      ...natifMetadataFields.date,
      labelType: DocumentLabelType.StampDate,
      saveLabel: (labelText: string) =>
        updateNatifMetadata({
          variables: {
            id: document.id,
            natifMetadata: { ...natifMetadata, date: labelText },
          },
        })
          .then(() => {
            natifMetadataFields.date.setValue(labelText)
          })
          .catch(() => {
            natifMetadataFields.date.setValue(labelText)
          }),
    },
  ]

  // Hook viewer instance to labeling features
  useLabelizer(
    {
      instance,
      labelType: nextLabelType,
      onLabeled: (labeledAnnotations, labeledType) => {
        labeledAnnotations.forEach(async label => {
          if (!label.text) {
            return
          }

          const labelField = labelableFields.find(
            field => field.labelType === labeledType
          )
          if (labelField) {
            labelField.focus()
            await labelField.saveLabel(label.text)
          }
        })
        setNextLabelType(undefined)
      },
    },
    [instance, nextLabelType]
  )

  // Enter annotation mode depending on the focused label type
  useEffect(
    () => {
      const focusedField = labelableFields.find(field => field.isFocused)
      if (focusedField) {
        setNextLabelType(focusedField.labelType)
      }
    },
    labelableFields.map(field => field.isFocused)
  )
  return (
    !loadingFileToken && (
      <Modal
        title={
          <Title
            documentId={document.id}
            documentStage={document.stage}
            documentTags={document.tags}
            customerFirstName={document.customer?.firstName}
            customerLastName={document.customer?.lastName}
            customerPublicId={document.customer?.encodedPublicId}
            documentCreatedAt={document.createdAt}
            customerCompanyName={document.customer?.companyName}
            documentRules={document.rules}
            subjectField={autosaveSubjectField}
            onRemoveDocumentDone={onRemoveDocumentDone}
          />
        }
        open
        onCancel={onClose}
        width="100vw"
        footer={null}
        style={styles.dialogStyle}
        styles={{ content: { borderRadius: 0 } }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <PDFLoader
              documentId={document.id}
              jwt={fileTokenData.document.fileToken}
              initialViewState={
                new NutrientViewer.ViewState({
                  showToolbar: false,
                  enableAnnotationToolbar: false,
                })
              }
              loaded={loadedInstance => {
                loadedInstance.setViewState(state =>
                  state.set('allowPrinting', false)
                )
                setInstance(loadedInstance)
              }}
            />
          </Col>
          <Col span={12}>
            <Row gutter={16}>
              <Col span={12}>
                <Form layout="vertical">
                  <RecipientMetadata
                    recipientMetadataFields={recipientMetadataFields}
                  />
                  <DocumentSender senderFields={senderFields} />
                  <DocumentTags document={document} onClose={onClose} />
                  <ScanMetadata scanMetadata={document.scanMetadata} />
                  <NatifMetadata natifMetadataFields={natifMetadataFields} />
                  <DocumentPaymentDetails
                    sender={document.senderMetadata}
                    documentId={document.id}
                    // biome-ignore lint/suspicious/noExplicitAny: ok in mailroom
                    paymentFields={paymentFields as any}
                  />
                </Form>
              </Col>
              <Col span={12}>
                <TagSelector document={document} />
              </Col>
            </Row>
          </Col>
        </Row>
      </Modal>
    )
  )
}

export default DocumentDialog
